import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";

/**
 * Label — form-field caption.
 *
 * Plain `<label>` (linked to inputs via `htmlFor`) + the disabled-input
 * styling shadcn consumers already expect. No Radix dependency — `<label>`
 * already gives keyboard + a11y wiring through the standard
 * label/control association, and the peer-disabled className handles the
 * "input is disabled, label dims with it" affordance via Tailwind's `peer`
 * modifier on a sibling input.
 *
 *   <Label htmlFor="email">Email</Label>
 *   <Input id="email" type="email" />
 *
 * Variants are intentionally minimal — Label sits inside Input/Textarea
 * pairings where the field carries the visual weight, not the label.
 */
export const labelVariants = cva(
  "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
  {
    variants: {
      size: {
        sm: "text-xs",
        default: "text-sm",
        lg: "text-base",
      },
    },
    defaultVariants: { size: "default" },
  },
);

export interface LabelProps
  extends React.LabelHTMLAttributes<HTMLLabelElement>,
    VariantProps<typeof labelVariants> {}

export const Label = React.forwardRef<HTMLLabelElement, LabelProps>(
  ({ className, size, ...props }, ref) => (
    <label ref={ref} className={cn(labelVariants({ size }), className)} {...props} />
  ),
);
Label.displayName = "Label";
